/**
 * Utilidades mínimas para leer claims de un JWT en cliente.
 *
 * Importante: NO valida la firma. Solo decodifica el payload para leer claims no
 * sensibles (p. ej. `exp`) y decidir si conviene refrescar la sesión antes de mandar
 * una petición. La autoridad sobre la validez del token sigue siendo del backend.
 */

/** Decodifica el payload (base64url) de un JWT. Devuelve null si no se puede parsear. */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * true si el access token está caducado (o lo estará dentro de `skewSeconds`).
 *
 * El margen (`skewSeconds`) evita mandar una petición con un token que caduca en
 * mitad del viaje. Si no hay `exp` legible, devolvemos false a propósito: dejamos que
 * el flujo reactivo de 401 lo gestione en vez de arriesgarnos a refrescar de más.
 */
export function isAccessTokenExpired(token: string, skewSeconds = 15): boolean {
  const payload = decodeJwtPayload(token);
  const exp = payload && typeof payload['exp'] === 'number' ? (payload['exp'] as number) : null;
  if (exp == null) return false;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return nowSeconds >= exp - skewSeconds;
}
